import type { ColumnDef, Row, TableState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { RowData } from "@tanstack/react-table";
import { useAtom, useSetAtom } from "jotai";
import MiniSearch from "minisearch";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NoResultIllustration } from "~/components/icons/no-result";
import { cn } from "~/components/ui/cn";
import { SortIcon } from "~/components/ui/icons/sort";
import { SortBottomIcon } from "~/components/ui/icons/sort-bottom";
import { SortTopIcon } from "~/components/ui/icons/sort-top";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { StoreCurrency } from "~/graphql/graphql.gen";
import { track } from "~/libs/analytics";
import { transformPrice } from "~/utils/currency";
import type { ProductIndex } from "~/utils/product";
import { SEARCH_PRODUCT_CONFIG, getSearchQuery } from "~/utils/product";
import { getStockValue } from "~/views/stock/stock-product-list-item";
import { StockProductVirtualList } from "~/views/stock/stock-product-virtual-list";
import { stockSortState } from "~/views/stock/stock-state";
import { activeStockIdState } from "~/views/stock/stock-state";
import { UpdateStockModal } from "~/views/stock/update-stock-modal";

export const PRODUCT_NAME_ACCESSOR_KEY = "productName";
export const SKU_ACCESSOR_KEY = "sku";
export const GTIN_ACCESSOR_KEY = "gtin";
export const TOTAL_ALERTS_COUNT_ACCESSOR_KEY = "totalAlertsCount";
export const POTENTIAL_REVENUE_ACCESSOR_KEY = "potentialRevenue";
export const ACTIVE_ALERTS_COUNT_ACCESSOR_KEY = "activeAlertsCount";
export const STOCK_ACCESSOR_KEY = "stock";

export type StockProduct = {
  id: string;
  image?: string | null;
  isDigital: boolean;
  productName: string;
  variantName?: string | null;
  sku?: string | null;
  gtin?: string | null;
  totalAlertsCount: number;
  potentialRevenue: number;
  activeAlertsCount: number;
  stockQuantity: number;
  stockId: string;
  currency: StoreCurrency;
};

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/consistent-type-definitions
  interface TableMeta<TData extends RowData> {
    activeStockId: string | undefined;
    canEdit: boolean;
  }
}

const customFilterFn = (
  row: Row<StockProduct>,
  _columnId: string,
  filterValue: GlobalFilterState,
) => {
  if (!filterValue.term) return true;

  const minisearch = new MiniSearch<ProductIndex>(SEARCH_PRODUCT_CONFIG);
  minisearch.add({
    id: row.original.id,
    name: row.original.productName,
    sku: row.original.sku ?? "",
    gtin: row.original.gtin ?? "",
    options: row.original.variantName ?? "",
  });

  let foundVariantIds: string[] | undefined = undefined;
  if (filterValue.term) {
    foundVariantIds = minisearch
      .search({
        prefix: true,
        queries: getSearchQuery(filterValue.term),
      })
      .map((searchResult) => searchResult.id as string);
  }

  if (foundVariantIds?.includes(row.original.id)) {
    return true;
  }

  return false;
};

export type GlobalFilterState = {
  term: string;
};

export function StockProductList({
  canEdit,
  data,
  pageRef,
  state,
}: {
  canEdit: boolean;
  data: StockProduct[];
  pageRef: React.RefObject<HTMLDivElement | null>;
  state: Partial<TableState> | undefined;
}) {
  const { i18n, t } = useTranslation();

  const [activeStockId, setActiveStockId] = useAtom(activeStockIdState);
  const setStockSort = useSetAtom(stockSortState);

  const columns = useMemo<ColumnDef<StockProduct>[]>(
    () => [
      {
        id: PRODUCT_NAME_ACCESSOR_KEY,
        accessorKey: "productName",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-left hover:bg-gray-50">
            <span>{t("stock.headers.name")}</span>
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div data-column="product" className="flex w-full gap-4 p-4">
            <div className="shrink-0">
              <img
                src={row.original.image ?? ""}
                alt=""
                className="size-14 rounded border-0.5 border-gray-300 object-cover"
              />
            </div>

            <div className="flex items-center min-[1200px]:hidden xl:flex">
              <div className="space-y-1">
                <p className="line-clamp-2 text-sm text-gray-900">
                  {row.original.productName}
                </p>
                {row.original.variantName ? (
                  <p className="space-x-1 text-xs text-gray-700">
                    <span>{row.original.variantName}</span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: SKU_ACCESSOR_KEY,
        accessorKey: "sku",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-left hover:bg-gray-50">
            <span>{t("stock.headers.sku")}</span>
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div
            data-column="sku"
            className="flex flex-col gap-2 break-all p-4 min-[1200px]:justify-center"
          >
            {/* Mobile header */}
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 min-[1200px]:hidden">
              {t("stock.headers.sku")}
            </div>
            {row.original.sku ? (
              <p className="text-sm text-gray-700">{row.original.sku}</p>
            ) : (
              <p className="text-sm text-gray-500">-</p>
            )}
          </div>
        ),
      },
      {
        id: GTIN_ACCESSOR_KEY,
        accessorKey: "gtin",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-left hover:bg-gray-50">
            <span>{t("stock.headers.gtin")}</span>
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div
            data-column="gtin"
            className="flex flex-col gap-2 break-all p-4 min-[1200px]:justify-center"
          >
            {/* Mobile header */}
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 min-[1200px]:hidden">
              {t("stock.headers.gtin")}
            </div>
            {row.original.gtin ? (
              <p className="text-sm text-gray-700">{row.original.gtin}</p>
            ) : (
              <p className="text-sm text-gray-500">-</p>
            )}
          </div>
        ),
      },
      {
        id: TOTAL_ALERTS_COUNT_ACCESSOR_KEY,
        accessorKey: "totalAlertsCount",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-right hover:bg-gray-50">
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
            <span>{t("stock.headers.total_alerts_count")}</span>
          </div>
        ),
        cell: ({ row }) => (
          <div
            data-column="total-alerts-count"
            className="flex items-center justify-end break-all p-4"
          >
            <p
              className={cn("px-2 py-1 text-right text-sm text-gray-900", {
                "rounded bg-beige-300 font-semibold text-orange-600":
                  row.original.totalAlertsCount > 0,
              })}
            >
              {row.original.totalAlertsCount}
            </p>
          </div>
        ),
      },
      {
        id: POTENTIAL_REVENUE_ACCESSOR_KEY,
        accessorKey: "potentialRevenue",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-right hover:bg-gray-50">
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
            <span>{t("stock.headers.potential_revenue")}</span>
          </div>
        ),
        cell: ({ row }) => (
          <div
            data-column="potential-revenue"
            className="flex items-center justify-end break-all p-4"
          >
            {row.original.potentialRevenue ? (
              <p className="text-right text-sm font-semibold text-gray-900">
                +{" "}
                {transformPrice(
                  row.original.potentialRevenue,
                  i18n.language === "fr" ? "fr" : "en",
                  row.original.currency.toString().toUpperCase(),
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-500">-</p>
            )}
          </div>
        ),
      },
      {
        id: ACTIVE_ALERTS_COUNT_ACCESSOR_KEY,
        accessorKey: "activeAlertsCount",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-right hover:bg-gray-50">
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
            <span>{t("stock.headers.active_alerts_count")}</span>
          </div>
        ),
        cell: ({ row }) => (
          <div
            data-column="active-alerts-count"
            className="flex items-center justify-end break-all p-4"
          >
            <p
              className={cn("px-2 py-1 text-right text-sm text-gray-900", {
                "rounded bg-beige-300 font-semibold text-orange-600":
                  row.original.activeAlertsCount > 0,
              })}
            >
              {row.original.activeAlertsCount}
            </p>
          </div>
        ),
      },
      {
        id: STOCK_ACCESSOR_KEY,
        accessorKey: "stockQuantity",
        header: ({ header }) => (
          <div className="group flex h-full cursor-pointer items-center justify-between gap-4 p-4 text-right hover:bg-gray-50">
            {header.column.getIsSorted() === "asc" ? (
              <SortTopIcon className="size-4 shrink-0" />
            ) : null}
            {header.column.getIsSorted() === "desc" ? (
              <SortBottomIcon className="size-4 shrink-0" />
            ) : null}
            <SortIcon
              className={cn("invisible size-4 shrink-0 group-hover:visible", {
                hidden: header.column.getIsSorted() !== false,
              })}
            />
            <span>{t("stock.headers.stock")}</span>
          </div>
        ),
        cell: ({ row, table }) => {
          const lowStock =
            typeof row.original.stockQuantity === "number" &&
            row.original.stockQuantity > 0 &&
            row.original.stockQuantity < 6;
          const outOfStock = row.original.stockQuantity === 0;
          return (
            <div
              data-column="stock"
              className="flex flex-col gap-2 break-all p-4 min-[1200px]:items-end min-[1200px]:justify-center"
            >
              {/* Mobile header */}
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 min-[1200px]:hidden">
                {t("stock.headers.stock")}
              </div>
              <div
                className={cn("text-right text-sm text-gray-900", {
                  "rounded font-semibold": lowStock || outOfStock,
                  "text-red-600": outOfStock,
                  "text-orange-600": lowStock,
                })}
              >
                {table.options.meta?.canEdit && !row.original.isDigital ? (
                  <Popover
                    open={
                      table.options.meta.activeStockId === row.original.stockId
                    }
                    onOpenChange={(open) => {
                      if (open === true) setActiveStockId(row.original.stockId);
                      if (open === false) setActiveStockId(undefined);
                    }}
                  >
                    <PopoverTrigger
                      type="button"
                      className="h-10 w-20 cursor-pointer rounded-lg border border-gray-300 p-2 text-right text-sm transition-colors duration-300 hover:border-gray-100 hover:bg-gray-100"
                      onClick={() => track("OpenModalStockUpdate", {})}
                    >
                      {getStockValue(row.original.stockQuantity)}
                    </PopoverTrigger>
                    <PopoverContent sideOffset={-54} className="w-[112px]">
                      <UpdateStockModal
                        previousStock={row.original.stockQuantity}
                        stockId={row.original.stockId}
                      />
                    </PopoverContent>
                  </Popover>
                ) : row.original.isDigital ? (
                  <Tooltip>
                    <TooltipTrigger>
                      <span className="mx-2 my-1 border-b border-dashed border-gray-300">
                        {getStockValue(row.original.stockQuantity)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      {t("stock.cant_edit_digital_stock")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="px-2 py-1">
                    {getStockValue(row.original.stockQuantity)}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [i18n.language, setActiveStockId, t],
  );

  const table = useReactTable({
    columns,
    data,
    state,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setStockSort,
    globalFilterFn: customFilterFn,
    meta: {
      activeStockId: activeStockId,
      canEdit,
    },
    debugTable: true,
  });

  if (table.getFilteredRowModel().rows.length === 0) {
    return (
      <>
        <div className="rounded border border-gray-100">
          {table.getHeaderGroups().map((headerGroup) => (
            <div
              key={headerGroup.id}
              data-table-row
              className="stock-product-list-header sticky top-0 z-[1] hidden rounded-t bg-white min-[1200px]:grid"
            >
              {/* Table row */}
              {headerGroup.headers.map((header) => (
                <div
                  key={header.id}
                  data-table-cell
                  className={cn(
                    "items-center bg-transparent text-xs font-semibold uppercase text-gray-700",
                  )}
                  {...(header.column.getCanSort()
                    ? { onClick: header.column.getToggleSortingHandler() }
                    : {})}
                >
                  {/* Table cell */}
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="mt-20 flex w-full flex-col items-center space-y-4">
          <NoResultIllustration className="size-20" />
          <div className="space-y-1 text-center">
            <h4 className="text-2xl font-bold text-gray-900">
              {t("stock.no_result.no_result_available")}
            </h4>
            <p className="text-sm">{t("stock.no_result.no_product_found")}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="rounded border border-gray-100">
      {table.getHeaderGroups().map((headerGroup) => (
        <div
          key={headerGroup.id}
          data-table-row
          className="stock-product-list-header sticky top-0 z-[1] hidden rounded-t border-b border-gray-300 bg-white min-[1200px]:grid"
        >
          {/* Table row */}
          {headerGroup.headers.map((header) => (
            <div
              key={header.id}
              data-table-cell
              className={cn(
                "items-center bg-transparent text-xs font-semibold uppercase text-gray-700",
              )}
              {...(header.column.getCanSort()
                ? { onClick: header.column.getToggleSortingHandler() }
                : {})}
            >
              {/* Table cell */}
              {header.isPlaceholder
                ? null
                : flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
            </div>
          ))}
        </div>
      ))}

      <StockProductVirtualList
        pageRef={pageRef}
        rows={table.getRowModel().rows}
      />
    </div>
  );
}
